"use client";

import {
    createContext,
    useContext,
    ReactNode,
    useState,
    useCallback,
    Dispatch,
    SetStateAction,
    useMemo,
} from "react";
import { FilesQuery, FoldersQuery } from "../types";
import { GetFoldersResponseType } from "./useGetFolders";
import {
    GetStoragePathResponseType,
    useGetStoragePath,
} from "./useGetStoragePath";
import { GetFilesResponseType } from "./useGetFiles";
import { StorageExplorerSettings } from "@/features/storage/useStorage";
import { useSyncInitialData } from "./useSyncInitialData";
import { useViewingPreferences } from "./useViewingPreferences";
import { ROOT_FOLDER_KEY, ViewingScope } from "./viewingPreferences";
import { View } from "../components/grids/types";
import { useSyncFolderIdToUrl } from "./useSyncFolderIdToUrl";
import { t } from "@/lib/localization";

interface StorageQueryContextType {
    foldersQuery: FoldersQuery;
    updateFoldersQuery: (changes: Partial<FoldersQuery>) => void;
    filesQuery: FilesQuery;
    updateFilesQuery: (changes: Partial<FilesQuery>) => void;
    parentFolder: GetFoldersResponseType["data"][0] | null;
    changeParentFolder: (
        nextFolder: GetFoldersResponseType["data"][0] | null
    ) => void;
    currentStoragePath?: GetStoragePathResponseType["folders"];
    loadingCurrentStoragePath: boolean;

    selectedFolders: GetFoldersResponseType["data"];
    setSelectedFolders: Dispatch<
        SetStateAction<GetFoldersResponseType["data"]>
    >;
    selectedFiles: GetFilesResponseType["data"];
    setSelectedFiles: Dispatch<SetStateAction<GetFilesResponseType["data"]>>;

    folderSettingsModalOpen: FolderForModal | null;
    setFolderSettingsModalOpen: Dispatch<SetStateAction<FolderForModal | null>>;
    deleteFolderModalOpen: FolderForModal | null;
    setDeleteFolderModalOpen: Dispatch<SetStateAction<FolderForModal | null>>;
    createYedaLmsCourseModalOpen: FolderForModal | null;
    setCreateYedaLmsCourseModalOpen: Dispatch<
        SetStateAction<FolderForModal | null>
    >;
    createFolderModalOpen: boolean;
    setCreateFolderModalOpen: Dispatch<SetStateAction<boolean>>;
    showSelectedItemsMenu: boolean;

    settings: StorageExplorerSettings;

    views: Record<ViewingScope, View>;
    updateView: (scope: ViewingScope, view: View) => void;
}

const StorageQueryContext = createContext<StorageQueryContextType | undefined>(
    undefined
);

export const useStorageQuery = () => {
    const ctx = useContext(StorageQueryContext);
    if (!ctx) throw new Error("useStorageQuery must be used inside Provider");
    return ctx;
};

export type { BaseQuery, FilesQuery, FoldersQuery } from "../types";

type FolderForModal = {
    id: string;
    name: string;
    presetId?: string | null;
};

export function StorageQueryProvider({
    children,
    settings,
    persistViewingOptions = false,
    syncFolderIdToUrl = false,
}: {
    children: ReactNode;
    settings: StorageExplorerSettings;
    persistViewingOptions?: boolean;
    syncFolderIdToUrl?: boolean;
}) {
    const [folderSettingsModalOpen, setFolderSettingsModalOpen] =
        useState<FolderForModal | null>(null);
    const [deleteFolderModalOpen, setDeleteFolderModalOpen] =
        useState<FolderForModal | null>(null);
    const [
        createYedaLmsCourseModalOpen,
        setCreateYedaLmsCourseModalOpen,
    ] = useState<FolderForModal | null>(null);
    const [createFolderModalOpen, setCreateFolderModalOpen] =
        useState<boolean>(false);

    const {
        foldersQuery,
        filesQuery,
        views,
        loadFolder,
        updateFoldersQuery,
        updateFilesQuery,
        updateView,
    } = useViewingPreferences({
        persist: persistViewingOptions,
        initialFilterBy: settings.files?.initialFilterBy,
    });
    const [parentFolder, setParentFolder] = useState<
        GetFoldersResponseType["data"][0] | null
    >(null);
    useSyncFolderIdToUrl(syncFolderIdToUrl, parentFolder);

    const [selectedFolders, setSelectedFolders] = useState<
        GetFoldersResponseType["data"]
    >([]);
    const [selectedFiles, setSelectedFiles] = useState<
        GetFilesResponseType["data"]
    >([]);

    const changeParentFolder = useCallback(
        (nextFolder: GetFoldersResponseType["data"][0] | null) => {
            loadFolder(nextFolder?.id ?? ROOT_FOLDER_KEY);
            setParentFolder(nextFolder);
        },
        [loadFolder]
    );

    useSyncInitialData({
        settings,
        setSelectedFolders,
        setSelectedFiles,
        changeParentFolder,
    });

    const showSelectedItemsMenu = useMemo(() => {
        if (settings.selectedItemsMenu.mode === "single-folder") {
            return !!parentFolder;
        } else if (settings.selectedItemsMenu.mode === "single-file") {
            return selectedFiles.length > 0;
        } else if (settings.selectedItemsMenu.mode === "multiple-items") {
            return selectedFiles.length > 0 || selectedFolders.length > 0;
        } else {
            return false;
        }
    }, [
        settings.selectedItemsMenu.mode,
        parentFolder,
        selectedFiles,
        selectedFolders,
    ]);

    const { data: currentStoragePath, isPending: loadingCurrentStoragePath } =
        useGetStoragePath({
            path: parentFolder?.path,
        });

    const value: StorageQueryContextType = {
        foldersQuery,
        updateFoldersQuery,
        filesQuery,
        updateFilesQuery,
        parentFolder,
        changeParentFolder,
        currentStoragePath,
        loadingCurrentStoragePath,

        selectedFolders,
        setSelectedFolders,
        selectedFiles,
        setSelectedFiles,

        folderSettingsModalOpen,
        setFolderSettingsModalOpen,
        createFolderModalOpen,
        setCreateFolderModalOpen,
        deleteFolderModalOpen,
        setDeleteFolderModalOpen,
        createYedaLmsCourseModalOpen,
        setCreateYedaLmsCourseModalOpen,
        showSelectedItemsMenu,

        settings,

        views,
        updateView,
    };

    return (
        <StorageQueryContext.Provider value={value}>
            {children}
        </StorageQueryContext.Provider>
    );
}
